/**
 * Diagnostico: as rotas do Auth que este sistema usa.
 *
 * Existe para responder "o manager fala com o Auth certo?" sem abrir codigo. Resolveu problema
 * real duas vezes: quando o Auth trocou de contrato em julho, e em 2026-10-06, quando foi a lista
 * do Reader que mostrou que `POST /check` existe no deploy.
 *
 * As duas listas vem do `openapi.json` de cada servico: e o que esta NO AR, nao o que o codigo
 * local diz. Essa diferenca ja custou um diagnostico errado nesta base.
 *
 * **Cada linha e uma STRING**, nao um registro. Tratar como objeto fez a tela renderizar uma
 * coluna por caractere do caminho - 110 rotas viraram uma matriz de letras. Por isso aqui e uma
 * lista, com filtro: com 110 rotas, procurar a olho e o mesmo que nao ter a tela.
 */

import { Button } from '@ledger-glass/react'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useState } from 'react'

import { fetchManagerPaths, fetchReaderPaths } from '../api/catalog'
import type { RotaDoAuth } from '../api/types'
import { Alert } from '../components/Alert'
import { Spinner } from '../components/Spinner'
import { humanizar } from '../errors/humanizar'

export function AuthCatalogPage() {
  const manager = useQuery({ queryKey: ['manager-paths'], queryFn: fetchManagerPaths })
  const reader = useQuery({ queryKey: ['reader-paths'], queryFn: fetchReaderPaths })
  const [filtro, setFiltro] = useState('')

  return (
    <>

      <div className="campo campo--filtro">
        <label htmlFor="filtro-de-rotas">Filtrar rotas</label>
        <input
          id="filtro-de-rotas"
          onChange={(evento) => setFiltro(evento.target.value)}
          placeholder="login, permissions, tenants…"
          value={filtro}
        />
      </div>

      <div className="registro__grupos">
        <ListaDeRotas
          consulta={manager}
          descricao="Escrita e administração. O manager usa para a hierarquia do tenant."
          filtro={filtro}
          titulo="Auth Manager"
        />
        <ListaDeRotas
          consulta={reader}
          descricao="Leitura e login. É por aqui que a sessão entra e as permissões são checadas."
          filtro={filtro}
          titulo="Auth Reader"
        />
      </div>
    </>
  )
}

function ListaDeRotas({
  titulo,
  descricao,
  filtro,
  consulta,
}: {
  titulo: string
  descricao: string
  filtro: string
  consulta: UseQueryResult<RotaDoAuth[]>
}) {
  const todas = consulta.data ?? []
  const termo = filtro.trim().toLowerCase()
  const visiveis = termo === '' ? todas : todas.filter((rota) => rota.toLowerCase().includes(termo))

  return (
    <section className="colecao">
      <header className="colecao__cabecalho">
        <h2 className="colecao__titulo">{titulo}</h2>
        <span className="colecao__contagem">
          {consulta.isPending
            ? '…'
            : termo === ''
              ? `${todas.length} rotas`
              : `${visiveis.length} de ${todas.length}`}
        </span>
      </header>

      <p className="colecao__descricao">{descricao}</p>

      {consulta.isPending ? (
        <div className="colecao__estado">
          <Spinner label="Consultando o Auth…" showLabel />
        </div>
      ) : consulta.error !== null ? (
        <div className="colecao__estado">
          <Alert
            tone="danger"
            action={
              <Button size="small" onClick={() => void consulta.refetch()}>
                Tentar de novo
              </Button>
            }
          >
            {humanizar(consulta.error)}
          </Alert>
        </div>
      ) : todas.length === 0 ? (
        <div className="colecao__estado">
          <Alert tone="warning">
            O serviço respondeu sem nenhuma rota. Isso não acontece com um Auth saudável.
          </Alert>
        </div>
      ) : visiveis.length === 0 ? (
        <div className="colecao__estado">
          <Alert tone="info">Nenhuma rota contém “{filtro}”.</Alert>
        </div>
      ) : (
        <ul className="rotas">
          {visiveis.map((rota) => (
            <li key={rota}>
              <code>{rota}</code>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
